import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Form,
  Input,
  Button,
  Upload,
  Typography,
  Row,
  Col,
  message,
  Steps,
  InputNumber,
  Switch,
  Alert,
  Spin,
  Space,
  Tag
} from 'antd';
import {
  UploadOutlined,
  StarOutlined,
  UserOutlined,
  MailOutlined,
  SettingOutlined,
  CheckCircleOutlined,
  CloudServerOutlined,
  TeamOutlined,
  ArrowLeftOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { HexColorPicker } from 'react-colorful';
import confetti from 'canvas-confetti';
import axiosConfig from '../../config/axiosConfig';
import { AuthContext } from '../../context/authContext';
import { useCompany } from '../../context/companyContext';
import './BecomeSolutionPartner.css';

const { Title, Text, Paragraph } = Typography;
const { Step } = Steps;

const BecomeSolutionPartner = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { currentUser } = useContext(AuthContext);
  const { companyData, isLoading: companyLoading, updateCustomerUuid } = useCompany();
  
  // Component states
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({});
  const [primaryColor, setPrimaryColor] = useState('#25D366');
  const [uploadedLogo, setUploadedLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  
  // Application status states
  const [existingApplication, setExistingApplication] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [applicationSubmitted, setApplicationSubmitted] = useState(false);
  const [applicationId, setApplicationId] = useState(null);

  // Helper function to get customeruuid from various sources
  const getCustomerUuid = () => {
    // Priority 1: companyData.customeruuid
    if (companyData?.customeruuid) {
      return companyData.customeruuid;
    }
    // Priority 2: sessionStorage 'customeruuid' key
    if (sessionStorage.getItem('customeruuid')) {
      return sessionStorage.getItem('customeruuid');
    }
    // Priority 3: sessionStorage 'user' key (JSON object with companyid)
    if (sessionStorage.getItem('user')) {
      try {
        const userObj = JSON.parse(sessionStorage.getItem('user'));
        if (userObj?.companyid) {
          return userObj.companyid;
        }
      } catch (error) {
        console.error('Error parsing user from sessionStorage:', error);
      }
    }
    // Priority 4: currentUser.customeruuid
    if (currentUser?.customeruuid) {
      return currentUser.customeruuid;
    }
    return null;
  };

  // Check for existing application on component mount
  useEffect(() => {
    console.log('Component mount - companyData:', companyData, 'currentUser:', currentUser, 'companyLoading:', companyLoading);
    
    if (!companyLoading) {
      const customerUuid = getCustomerUuid();
      
      console.log('Using customerUuid for status check:', customerUuid);
      
      if (customerUuid) {
        checkExistingApplication(customerUuid);
      } else {
        console.log('No customerUuid found, showing form');
        setCheckingStatus(false);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyData, currentUser, companyLoading]);

  const checkExistingApplication = async (customerUuid) => {
    try {
      setCheckingStatus(true);
      const response = await axiosConfig.get(`/dashboard/partner-request?customeruuid=${customerUuid}`);
      console.log('Partner request response:', response.data);
      
      if (response.data?.status === true && response.data?.entity?.requests?.length > 0) {
        // Find the application for the current customer
        const userApplication = response.data.entity.requests.find(
          req => req.customeruuid === customerUuid
        );
        
        if (userApplication) {
          // Parse business_config if it's a string
          if (typeof userApplication.business_config === 'string') {
            try {
              userApplication.business_config = JSON.parse(userApplication.business_config);
            } catch (e) {
              console.warn('Failed to parse business_config:', e);
              userApplication.business_config = {};
            }
          }
          
          // Parse branding_config if it's a string
          if (typeof userApplication.branding_config === 'string') {
            try {
              userApplication.branding_config = JSON.parse(userApplication.branding_config);
            } catch (e) {
              console.warn('Failed to parse branding_config:', e);
              userApplication.branding_config = {};
            }
          }
          
          // Add created_at if not present (use current date as fallback)
          if (!userApplication.created_at) {
            userApplication.created_at = new Date().toISOString();
          }
          
          // Add support_email if not present but available in other fields
          if (!userApplication.support_email && userApplication.branding_config?.support_email) {
            userApplication.support_email = userApplication.branding_config.support_email;
          }
          
          // Normalize status - the new response seems to have string status values
          let normalizedStatus = 0;
          if (typeof userApplication.status === 'string') {
            const status = userApplication.status.toLowerCase();
            if (status === 'pending' || status === 'pending_review') normalizedStatus = 0;
            else if (status === 'approved') normalizedStatus = 1;
            else if (status === 'rejected') normalizedStatus = 2;
          } else if (typeof userApplication.status === 'number') {
            normalizedStatus = userApplication.status;
          }
          
          userApplication.normalizedStatus = normalizedStatus;
          setExistingApplication(userApplication);
          console.log('Found existing application:', userApplication);
        } else {
          console.log('No application found for customerUuid:', customerUuid);
          setExistingApplication(null);
        }
      } else {
        console.log('No applications found in response');
        setExistingApplication(null);
      }
    } catch (error) {
      console.error('Error checking existing application:', error);
      setExistingApplication(null);
    } finally {
      setCheckingStatus(false);
    }
  };

  const steps = [
    { title: 'Partner Information', icon: <UserOutlined /> },
    { title: 'Branding & Configuration', icon: <SettingOutlined /> },
    { title: 'Review & Submit', icon: <CheckCircleOutlined /> }
  ];

  const handleNext = async () => {
    try {
      const values = await form.validateFields();
      setFormData({ ...formData, ...values });
      setCurrentStep(currentStep + 1);
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const handlePrev = () => {
    setCurrentStep(currentStep - 1);
  };

  const triggerConfetti = () => {
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 }
    });
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      
      const slug = formData.partner_name
        ?.toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '') || '';

      const submitData = new FormData();
      submitData.append('customeruuid', getCustomerUuid() || '');
      submitData.append('slug', slug);
      submitData.append('partner_name', formData.partner_name || '');
      submitData.append('support_email', formData.support_email || '');
      submitData.append('primary_color', primaryColor);
      
      const brandingConfig = JSON.stringify({
        theme: 'light',
        font: 'Arial',
        primary_color: primaryColor,
        company_name: formData.partner_name || ''
      });
      submitData.append('branding_config', brandingConfig);
      
      const businessConfig = JSON.stringify({
        port: formData.smtp_port || 465,
        smtp_host: formData.smtp_host || '',
        ssl_enabled: formData.ssl_enabled || true
      });
      submitData.append('business_config', businessConfig);
      
      if (uploadedLogo) {
        submitData.append('partner_logo', uploadedLogo);
      }

      const response = await axiosConfig.post('/dashboard/send-partner-request', submitData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.status === true) {
        // Update company context with new customeruuid if provided
        if (response.data.entity?.customeruuid) {
          const newCustomerUuid = response.data.entity.customeruuid;
          console.log('Partner application successful, updating customeruuid to:', newCustomerUuid);
          
          // Update company context and sessionStorage
          updateCustomerUuid(newCustomerUuid);
          
          // Trigger confetti and show success
          triggerConfetti();
          setApplicationSubmitted(true);
          setApplicationId(response.data.applicationId || 'APP-' + Date.now());
          message.success('Your solution partner application has been submitted successfully!');
          
          // Check existing application with new UUID after a short delay
          setTimeout(() => {
            checkExistingApplication(newCustomerUuid);
          }, 1500);
        } else {
          triggerConfetti();
          setApplicationSubmitted(true);
          setApplicationId(response.data.applicationId || 'APP-' + Date.now());
          message.success('Your solution partner application has been submitted successfully!');
          
          // Refresh status after submission
          setTimeout(() => {
            const uuid = getCustomerUuid();
            if (uuid) checkExistingApplication(uuid);
          }, 2000);
        }
      } else {
        if (response.data.message?.includes('duplicate key') || response.data.message?.includes('violates unique constraint')) {
          message.warning('You have already submitted a partner application. Loading your status...');
          const uuid = getCustomerUuid();
          if (uuid) {
            setTimeout(() => checkExistingApplication(uuid), 1000);
          }
        } else {
          message.error(response.data.message || 'Failed to submit application');
        }
      }
    } catch (error) {
      console.error('Error submitting application:', error);
      message.error('An error occurred while submitting your application');
    } finally {
      setLoading(false);
    }
  };

  const uploadProps = {
    name: 'file',
    multiple: false,
    maxCount: 1,
    accept: '.jpg,.jpeg,.png',
    beforeUpload: (file) => {
      // Validate file size (5MB limit)
      const isLt5M = file.size / 1024 / 1024 < 5;
      if (!isLt5M) {
        message.error('Image must be smaller than 5MB!');
        return false;
      }
      
      // Validate file type - only JPEG and PNG
      const isValidType = ['image/jpeg', 'image/jpg', 'image/png'].includes(file.type);
      if (!isValidType) {
        message.error('You can only upload JPEG and PNG files!');
        return false;
      }
      
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setLogoPreview(previewUrl);
      setUploadedLogo(file);
      form.setFieldsValue({ partner_logo: file.name });
      // message.success(`${file.name} selected successfully`);
      return false;
    },
    onRemove: () => {
      if (logoPreview) {
        URL.revokeObjectURL(logoPreview);
      }
      setLogoPreview(null);
      setUploadedLogo(null);
      form.setFieldsValue({ partner_logo: null });
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="form-section">
            <h3><UserOutlined className="form-section-icon" />Partner Information</h3>
            <Row gutter={[24, 24]}>
              <Col xs={24} lg={12}>
                <Form.Item
                  name="partner_name"
                  label="Company Name"
                  rules={[
                    { required: true, message: 'Please enter your company name' },
                    { max: 64, message: 'Company name must be 64 characters or less' },
                    { min: 2, message: 'Company name must be at least 2 characters' },
                    {
                      validator: (_, value) => {
                        if (!value) return Promise.resolve();
                        
                        // Check if it's only spaces
                        if (value.trim().length === 0) {
                          return Promise.reject(new Error('Company name cannot be only spaces'));
                        }
                        
                        // Check for valid characters (alphanumeric, spaces, and common business symbols)
                        if (!/^[a-zA-Z0-9\s\-&.,()]+$/.test(value)) {
                          return Promise.reject(new Error('Company name contains invalid characters'));
                        }
                        
                        return Promise.resolve();
                      }
                    }
                  ]}
                  hasFeedback
                >
                  <Input 
                    size="large" 
                    placeholder="Enter your company name" 
                    prefix={<TeamOutlined />}
                    maxLength={64}
                    showCount
                  />
                </Form.Item>
              </Col>
              <Col xs={24} lg={12}>
                <Form.Item
                  name="support_email"
                  label="Support Email"
                  rules={[
                    { required: true, message: 'Please enter your support email' },
                    { type: 'email', message: 'Please enter a valid email address' }
                  ]}
                >
                  <Input size="large" placeholder="support@yourcompany.com" prefix={<MailOutlined />} />
                </Form.Item>
              </Col>
            </Row>
          </div>
        );

      case 1:
        return (
          <div className="form-section">
            <h3><SettingOutlined className="form-section-icon" />Branding & Configuration</h3>
            <Row gutter={[24, 24]}>
              <Col xs={24} lg={12}>
                <Form.Item 
                  name="partner_logo" 
                  label="Company Logo"
                  rules={[
                    { required: true, message: 'Please upload your company logo' }
                  ]}
                >
                  <div className="upload-area">
                    {logoPreview ? (
                      <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                        <div style={{ 
                          display: 'inline-block',
                          padding: '8px',
                          border: '2px dashed #d9d9d9',
                          borderRadius: '8px',
                          backgroundColor: '#fafafa'
                        }}>
                          <img 
                            src={logoPreview} 
                            alt="Logo Preview" 
                            style={{ 
                              maxWidth: '150px', 
                              maxHeight: '150px', 
                              objectFit: 'contain',
                              borderRadius: '4px'
                            }} 
                          />
                        </div>
                        <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                          {uploadedLogo?.name}
                        </div>
                      </div>
                    ) : null}
                    <Upload {...uploadProps} maxCount={1}>
                      <div>
                        <UploadOutlined className="upload-icon" />
                        <div style={{ marginTop: 8, fontSize: '16px', fontWeight: 500 }}>
                          {logoPreview ? 'Change logo' : 'Click to upload logo'}
                        </div>
                        <div style={{ marginTop: 4, fontSize: '14px', color: '#6b7280' }}>
                          JPEG, PNG (max 5MB)
                        </div>
                      </div>
                    </Upload>
                  </div>
                </Form.Item>
              </Col>
              <Col xs={24} lg={12}>
                <Form.Item label="Primary Brand Color">
                  <div className="color-picker-wrapper">
                    <div className="color-preview" style={{ backgroundColor: primaryColor }} />
                    <div style={{ flex: 1 }}>
                      <HexColorPicker 
                        color={primaryColor} 
                        onChange={setPrimaryColor}
                        style={{ width: '100%', height: '120px' }}
                      />
                      <Input 
                        value={primaryColor}
                        onChange={(e) => setPrimaryColor(e.target.value)}
                        placeholder="#25D366"
                        style={{ marginTop: 8 }}
                      />
                    </div>
                  </div>
                </Form.Item>
              </Col>
            </Row>

            <h3 style={{ marginTop: 40 }}>
              <CloudServerOutlined className="form-section-icon" />SMTP Configuration
            </h3>
            <Row gutter={[24, 24]}>
              <Col xs={24} lg={8}>
                <Form.Item
                  name="smtp_host"
                  label="SMTP Host"
                  rules={[
                    { required: true, message: 'Please enter SMTP host' },
                    {
                      pattern: /^[a-zA-Z0-9][a-zA-Z0-9-]*(\.[a-zA-Z0-9][a-zA-Z0-9-]*)+$/,
                      message: 'Please enter a valid domain format (e.g., smtp.gmail.com)'
                    }
                  ]}
                  hasFeedback
                  extra="Example: smtp.gmail.com, mail.yourcompany.com"
                >
                  <Input 
                    size="large" 
                    placeholder="smtp.yourcompany.com"
                    maxLength={100}
                    onBlur={() => {
                      // Trigger validation on blur
                      form.validateFields(['smtp_host']);
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} lg={8}>
                <Form.Item 
                  name="smtp_port" 
                  label="SMTP Port" 
                  initialValue={465}
                  rules={[
                    { required: true, message: 'Please enter SMTP port' },
                    {
                      validator: (_, value) => {
                        if (!value) return Promise.resolve();
                        
                        // Check if it's a valid number
                        if (isNaN(value) || value < 1 || value > 65535) {
                          return Promise.reject(new Error('Port must be between 1 and 65535'));
                        }
                        
                        // Warn about uncommon ports
                        const commonPorts = [25, 465, 587, 993, 995];
                        if (!commonPorts.includes(parseInt(value))) {
                          message.warning('This is not a common SMTP port. Common ports are: 25, 465 (SSL), 587 (TLS)');
                        }
                        
                        return Promise.resolve();
                      }
                    }
                  ]}
                  extra="Common ports: 25, 465 (SSL), 587 (TLS)"
                >
                  <InputNumber 
                    size="large" 
                    style={{ width: '100%' }}
                    min={1}
                    max={65535}
                    placeholder="465"
                    parser={(value) => value.replace(/[^0-9]/g, '')}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} lg={8}>
                <Form.Item
                  name="ssl_enabled"
                  label="SSL Enabled"
                  valuePropName="checked"
                  initialValue={true}
                  extra="Enable SSL/TLS encryption for secure email transmission"
                  rules={[
                    {
                      validator: (_, value) => {
                        // This is more of an informational validation
                        if (value === false) {
                          message.warning('Disabling SSL is not recommended for production use');
                        }
                        return Promise.resolve();
                      }
                    }
                  ]}
                >
                  <Switch size="default" />
                </Form.Item>
              </Col>
            </Row>
          </div>
        );

      case 2:
        return (
          <div className="form-section">
            <h3><CheckCircleOutlined className="form-section-icon" />Review Your Application</h3>
            <div style={{ 
              background: '#f8fafc', 
              padding: '24px', 
              borderRadius: '8px', 
              marginBottom: '32px',
              border: '1px solid #e2e8f0'
            }}>
              <h4 style={{ color: '#374151', fontWeight: 600, marginBottom: '20px', fontSize: '16px' }}>
                Application Summary
              </h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: 500 }}>COMPANY NAME</span>
                  <span style={{ fontSize: '14px', color: '#111827', fontWeight: 500 }}>{formData.partner_name}</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: 500 }}>SUPPORT EMAIL</span>
                  <span style={{ fontSize: '14px', color: '#111827', fontWeight: 500 }}>{formData.support_email}</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: 500 }}>PRIMARY COLOR</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ 
                      display: 'inline-block', 
                      width: '16px', 
                      height: '16px', 
                      backgroundColor: primaryColor,
                      borderRadius: '4px',
                      border: '1px solid #d1d5db'
                    }}></span>
                    <span style={{ fontSize: '14px', color: '#111827', fontWeight: 500 }}>{primaryColor}</span>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: 500 }}>LOGO</span>
                  <span style={{ fontSize: '14px', color: '#111827', fontWeight: 500 }}>
                    {uploadedLogo ? uploadedLogo.name : 'Not uploaded'}
                  </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: 500 }}>SMTP HOST</span>
                  <span style={{ fontSize: '14px', color: '#111827', fontWeight: 500 }}>{formData.smtp_host}</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: 500 }}>SMTP PORT</span>
                  <span style={{ fontSize: '14px', color: '#111827', fontWeight: 500 }}>{formData.smtp_port || 465}</span>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const renderSuccessPage = () => (
    <div className="success-container">
      <div className="success-card">
        <CheckCircleOutlined className="success-icon" />
        <h1 className="success-title"> Application Submitted! ✅</h1>
        <h2 className="success-subtitle">Your Partner Application is Submitted!</h2>
        <Paragraph style={{ fontSize: '1.1rem', color: '#6b7280', marginBottom: '30px' }}>
          Thank you for your interest in becoming a MessageOS Solution Partner. 
          Your application has been received and is currently under review.
        </Paragraph>
        
        <div className="success-details">
          <h4>Application Details:</h4>
          <div className="success-detail-item">
            <strong>Application ID:</strong> {applicationId}
          </div>
          <div className="success-detail-item">
            <strong>Company Name:</strong> {formData.partner_name}
          </div>
          <div className="success-detail-item">
            <strong>Support Email:</strong> {formData.support_email}
          </div>
          <div className="success-detail-item">
            <strong>Submission Date:</strong> {new Date().toLocaleDateString()}
          </div>
        </div>

        <Alert
          message="What happens next?"
          description={
            <ul style={{ paddingLeft: 20, marginBottom: 0 }}>
              <li>Our team will review your application within 2-3 business days</li>
              <li>You will receive an email notification about the status</li>
              <li>If approved, you'll get access to partner resources and onboarding materials</li>
              <li>Our partner success team will reach out to guide you through the setup process</li>
            </ul>
          }
          type="info"
          showIcon
          style={{ marginBottom: 30, textAlign: 'left' }}
        />

        <div className="success-actions">
          <Button 
            size="large"
            onClick={() => navigate('/dashboard')}
            icon={<ArrowLeftOutlined />}
          >
            Back to Dashboard
          </Button>
        </div>
      </div>
    </div>
  );

  const renderExistingApplicationStatus = () => {
    const getStatusConfig = (status) => {
      const normalizedStatus = existingApplication.normalizedStatus !== undefined 
        ? existingApplication.normalizedStatus 
        : status;
      
      switch (normalizedStatus) {
        case 0:
          return { 
            color: 'orange', 
            text: 'Under Review', 
            icon: '🔍', 
            bgColor: 'linear-gradient(135deg, #fff8e1 0%, #fff3c4 100%)', 
            borderColor: '#ffb74d',
            accentColor: '#ff9800'
          };
        case 1:
          return { 
            color: 'green', 
            text: 'Approved', 
            icon: '🎉', 
            bgColor: 'linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)', 
            borderColor: '#81c784',
            accentColor: '#4caf50'
          };
        case 2:
          return { 
            color: 'red', 
            text: 'Rejected ❌', 
            icon: '📋', 
            bgColor: 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)', 
            borderColor: '#e57373',
            accentColor: '#f44336'
          };
        default:
          return { 
            color: 'gray', 
            text: 'Unknown', 
            icon: '❓', 
            bgColor: 'linear-gradient(135deg, #f5f5f5 0%, #eeeeee 100%)', 
            borderColor: '#bdbdbd',
            accentColor: '#757575'
          };
      }
    };

    const statusConfig = getStatusConfig(existingApplication.normalizedStatus || existingApplication.status);

    return (
      <div style={{ 
        padding: '24px', 
        // maxWidth: '800px', 
        margin: '0 auto'
      }}>
        <Card 
          style={{ 
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            border: '1px solid #d9d9d9'
          }}
        >
          {/* Header Section */}
          <div style={{ 
            padding: '24px',
            textAlign: 'center',
            borderBottom: '1px solid #f0f0f0'
          }}>
            <div style={{ 
              fontSize: '48px', 
              marginBottom: '12px'
            }}>
              {statusConfig.icon}
            </div>
            <Title level={2} style={{ 
              margin: 0, 
              color: '#262626',
              marginBottom: '8px'
            }}>
              Application {statusConfig.text}
            </Title>
            <Text style={{ 
              color: '#8c8c8c'
            }}>
              Your solution partner application status
            </Text>
          </div>
          
          {/* Application Details Section */}
          <div style={{ padding: '24px' }}>
            <Title level={4} style={{ 
              marginBottom: '16px',
              color: '#262626'
            }}>
              Application Details
            </Title>

            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12}>
                <div style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  padding: '12px',
                  background: '#fafafa',
                  borderRadius: '6px',
                  border: '1px solid #f0f0f0'
                }}>
                  <TeamOutlined style={{ 
                    color: '#1890ff', 
                    fontSize: '16px',
                    marginRight: '8px'
                  }} />
                  <div>
                    <div style={{ fontSize: '12px', color: '#8c8c8c' }}>Company Name</div>
                    <div style={{ fontSize: '14px', color: '#262626', fontWeight: 500 }}>
                      {existingApplication.partner_name}
                    </div>
                  </div>
                </div>
              </Col>
                
              <Col xs={24} sm={12}>
                <div style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  padding: '12px',
                  background: '#fafafa',
                  borderRadius: '6px',
                  border: '1px solid #f0f0f0'
                }}>
                  <MailOutlined style={{ 
                    color: '#1890ff', 
                    fontSize: '16px',
                    marginRight: '8px'
                  }} />
                  <div>
                    <div style={{ fontSize: '12px', color: '#8c8c8c' }}>Support Email</div>
                    <div style={{ fontSize: '14px', color: '#262626', fontWeight: 500 }}>
                      {existingApplication.support_email || 'Not provided'}
                    </div>
                  </div>
                </div>
              </Col>

              <Col xs={24} sm={12}>
                <div style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  padding: '12px',
                  background: '#fafafa',
                  borderRadius: '6px',
                  border: '1px solid #f0f0f0'
                }}>
                  <CheckCircleOutlined style={{ 
                    color: statusConfig.color === 'orange' ? '#fa8c16' : statusConfig.color === 'green' ? '#52c41a' : '#f5222d', 
                    fontSize: '16px',
                    marginRight: '8px'
                  }} />
                  <div>
                    <div style={{ fontSize: '12px', color: '#8c8c8c' }}>Status</div>
                    <Tag color={statusConfig.color} style={{ margin: 0 }}>
                      {statusConfig.text}
                    </Tag>
                  </div>
                </div>
              </Col>

              <Col xs={24} sm={12}>
                <div style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  padding: '12px',
                  background: '#fafafa',
                  borderRadius: '6px',
                  border: '1px solid #f0f0f0'
                }}>
                  <span style={{ fontSize: '16px', marginRight: '8px' }}>📅</span>
                  <div>
                    <div style={{ fontSize: '12px', color: '#8c8c8c' }}>Submitted</div>
                    <div style={{ fontSize: '14px', color: '#262626', fontWeight: 500 }}>
                      {existingApplication.created_at ? 
                        new Date(existingApplication.created_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        }) : 'Date not available'
                      }
                    </div>
                  </div>
                </div>
              </Col>

              {existingApplication.primary_color && (
                <Col xs={24} sm={12}>
                  <div style={{ 
                    display: 'flex',
                    alignItems: 'center',
                    padding: '12px',
                    background: '#fafafa',
                    borderRadius: '6px',
                    border: '1px solid #f0f0f0'
                  }}>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      backgroundColor: existingApplication.primary_color,
                      borderRadius: '4px',
                      border: '1px solid #d9d9d9',
                      marginRight: '8px'
                    }}></div>
                    <div>
                      <div style={{ fontSize: '12px', color: '#8c8c8c' }}>Brand Color</div>
                      <div style={{ fontSize: '14px', color: '#262626', fontWeight: 500 }}>
                        {existingApplication.primary_color}
                      </div>
                    </div>
                  </div>
                </Col>
              )}

              {(existingApplication.business_config?.smtp_host || existingApplication.business_config?.host) && (
                <Col xs={24} sm={12}>
                  <div style={{ 
                    display: 'flex',
                    alignItems: 'center',
                    padding: '12px',
                    background: '#fafafa',
                    borderRadius: '6px',
                    border: '1px solid #f0f0f0'
                  }}>
                    <CloudServerOutlined style={{ 
                      color: '#1890ff', 
                      fontSize: '16px',
                      marginRight: '8px'
                    }} />
                    <div>
                      <div style={{ fontSize: '12px', color: '#8c8c8c' }}>SMTP Host</div>
                      <div style={{ fontSize: '14px', color: '#262626', fontWeight: 500 }}>
                        {existingApplication.business_config.smtp_host || existingApplication.business_config.host || 'Not configured'}
                      </div>
                    </div>
                  </div>
                </Col>
              )}

              {existingApplication.slug && (
                <Col xs={24} sm={12}>
                  <div style={{ 
                    display: 'flex',
                    alignItems: 'center',
                    padding: '12px',
                    background: '#fafafa',
                    borderRadius: '6px',
                    border: '1px solid #f0f0f0'
                  }}>
                    <span style={{ fontSize: '16px', marginRight: '8px' }}>🔗</span>
                    <div>
                      <div style={{ fontSize: '12px', color: '#8c8c8c' }}>Partner Slug</div>
                      <div style={{ fontSize: '14px', color: '#262626', fontWeight: 500 }}>
                        {existingApplication.slug}
                      </div>
                    </div>
                  </div>
                </Col>
              )}
            </Row>
            
            {existingApplication.rejection_reason && (
              <Alert
                message="Rejection Reason"
                description={existingApplication.rejection_reason}
                type="error"
                showIcon
                style={{ marginTop: '16px' }}
              />
            )}

            {/* Status Messages */}
            {(existingApplication.normalizedStatus === 0 || existingApplication.status === 'Pending') && (
              <Alert
                message="Application Under Review"
                description="Your application is currently being reviewed by our team. You will receive an email notification once a decision is made. This process typically takes 2-3 business days."
                type="info"
                showIcon
                style={{ marginTop: '16px' }}
              />
            )}

            {(existingApplication.normalizedStatus === 1 || existingApplication.status === 'Approved') && (
              <Alert
                message="Congratulations! Application Approved"
                description="Your partner application has been approved. You can now access the Solution Partner Admin Dashboard to manage your partner account and start onboarding customers."
                type="success"
                showIcon
                style={{ marginTop: '16px' }}
              />
            )}

            {(existingApplication.normalizedStatus === 2 || existingApplication.status === 'Rejected') && (
              <Alert
                message="Application Needs Revision"
                description="Unfortunately, your application was not approved at this time. Please review the rejection reason above and submit a new application addressing the feedback provided."
                type="error"
                showIcon
                style={{ marginTop: '16px' }}
              />
            )}
          </div>

          {/* Action Buttons */}
          <div style={{ 
            padding: '24px',
            borderTop: '1px solid #f0f0f0',
            textAlign: 'center'
          }}>
            <Space wrap>
              {(existingApplication.normalizedStatus === 2 || existingApplication.status === 'Rejected') && (
                <Button 
                  type="primary" 
                  onClick={() => {
                    setExistingApplication(null);
                    setCurrentStep(0);
                    setFormData({});
                    form.resetFields();
                  }}
                  icon={<StarOutlined />}
                >
                  Submit New Application
                </Button>
              )}
              
              {(existingApplication.normalizedStatus === 1 || existingApplication.status === 'Approved') && (
                <Button 
                  type="primary" 
                  onClick={() => navigate('/dashboard/solution-partner-admin')}
                  icon={<StarOutlined />}
                >
                  Go to Partner Dashboard
                </Button>
              )}
              
              <Button 
                onClick={() => {
                  const uuid = getCustomerUuid();
                  if (uuid) checkExistingApplication(uuid);
                }}
                icon={<ReloadOutlined />}
              >
                Refresh Status
              </Button>
              
              <Button 
                onClick={() => navigate('/dashboard')}
                icon={<ArrowLeftOutlined />}
              >
                Back to Dashboard
              </Button>
            </Space>
          </div>
        </Card>
      </div>
    );
  };

  // Show loading while checking status
  if (checkingStatus) {
    return (
      <div style={{ padding: '60px 24px', textAlign: 'center', maxWidth: '600px', margin: '0 auto' }}>
        <Card style={{ borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
          <div style={{ padding: '40px 20px' }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>🔍</div>
            <Title level={3} style={{ marginBottom: 16, color: '#1f2937' }}>Checking Application Status</Title>
            <Paragraph style={{ marginBottom: 32, color: '#6b7280', fontSize: '16px' }}>
              We're checking if you have any existing partner applications...
            </Paragraph>
            <Spin size="large" />
          </div>
        </Card>
      </div>
    );
  }

  // Show existing application status if found
  if (existingApplication && !applicationSubmitted) {
    return renderExistingApplicationStatus();
  }

  // Show success page if application is submitted
  if (applicationSubmitted) {
    return renderSuccessPage();
  }

  // Show the form for new applications
  return (
    <div className="partner-application-container">
      <div className="partner-application-wrapper">
        {/* Header */}
        <div className="partner-header">
          <div className="partner-header-content">
            <StarOutlined className="partner-header-icon" />
            <h1>Become a Solution Partner</h1>
            <p>
              Join our partner network and help businesses leverage the power of WhatsApp Business API. 
              Fill out the form below to start your journey as a MessageOS Solution Partner.
            </p>
          </div>
        </div>

        {/* Form Container */}
        <div className="partner-form-container">
          {/* Steps */}
          <Steps current={currentStep} className="partner-steps" size="default">
            {steps.map(item => (
              <Step key={item.title} title={item.title} icon={item.icon} />
            ))}
          </Steps>

          {/* Form */}
          <Form
            form={form}
            layout="vertical"
            initialValues={{
              smtp_port: 465,
              ssl_enabled: true,
            }}
          >
            {renderStepContent()}

            {/* Navigation */}
            <div className="form-navigation">
              <div>
                {currentStep > 0 && (
                  <Button 
                    size="large" 
                    onClick={handlePrev}
                    className="nav-button nav-button-secondary"
                  >
                    Previous
                  </Button>
                )}
              </div>
              <div>
                {currentStep < steps.length - 1 ? (
                  <Button 
                    type="primary" 
                    size="large" 
                    onClick={handleNext}
                    className="nav-button nav-button-primary"
                  >
                    Next
                  </Button>
                ) : (
                  <Button 
                    type="primary" 
                    size="large" 
                    loading={loading}
                    onClick={handleSubmit}
                    className="nav-button nav-button-primary"
                  >
                    Submit Application
                  </Button>
                )}
              </div>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default BecomeSolutionPartner;
